import React from "react";
import { Helmet } from "react-helmet-async";
import { APP_STORE_URL, PLAY_STORE_URL } from "../../constants/appStores";

const SITE_URL = "https://www.campusgenie.app";
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;
const SITE_NAME = "CampusGenie";
const TWITTER_HANDLE = "@campusgenie";

export default function SEO({
  title,
  description,
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  publishedTime,
  modifiedTime,
  author,
  tags,
  noindex = false,
  structuredData,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - The App That Gets You In, Then Gets You Through`;
  const canonicalUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const fullImage = image?.startsWith("http") ? image : `${SITE_URL}${image}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_NG" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      
      {/* Article specific meta (for blog posts) */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && author && (
        <meta property="article:author" content={author} />
      )}
      {type === "article" && tags?.map((tag, index) => (
        <meta key={index} property="article:tag" content={tag} />
      ))}
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

export function generateOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "CampusGenie",
    "url": SITE_URL,
    "logo": `${SITE_URL}/logo.png`,
    "description": "CampusGenie is the single app that stays with students from Admisson preparation to university graduation.",
    "foundingDate": "2023",
    "founders": [{
      "@type": "Person",
      "name": "CampusGenie Team"
    }],
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "NG"
    },
    "sameAs": [
      "https://twitter.com/campusgenie",
      "https://instagram.com/campusgenie",
      "https://facebook.com/campusgenie",
      "https://linkedin.com/company/campusgenie",
      APP_STORE_URL,
      PLAY_STORE_URL
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer support",
      "email": "hello@campusgenie.app"
    }
  };
}

export function generateMobileAppSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "MobileApplication",
    "name": "CampusGenie",
    "operatingSystem": "iOS, Android",
    "applicationCategory": "EducationalApplication",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "NGN"
    },
    "description": "CampusGenie is the single app that stays with students from Admisson preparation to university graduation. Features include JAMB & WAEC past questions, CBT simulation, AI study planner, university tools, internships, and hostel booking.",
    "downloadUrl": [
      APP_STORE_URL,
      PLAY_STORE_URL
    ],
    "author": {
      "@type": "Organization",
      "name": "CampusGenie",
      "url": SITE_URL
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "1500",
      "bestRating": "5",
      "worstRating": "1"
    },
    "screenshot": `${SITE_URL}/screenshots/app-preview.png`
  };
}

export function generateBreadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${SITE_URL}${item.url}`
    }))
  };
}

export function generateArticleSchema(article) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt || article.description,
    "image": article.image || DEFAULT_IMAGE,
    "datePublished": article.publishedTime,
    "dateModified": article.modifiedTime || article.publishedTime,
    "author": {
      "@type": "Organization",
      "name": "CampusGenie",
      "url": SITE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "CampusGenie",
      "logo": {
        "@type": "ImageObject",
        "url": `${SITE_URL}/logo.png`
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${SITE_URL}/blog/${article.id}`
    },
    "keywords": article.tags?.join(", ")
  };
}

export function generateFAQSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}

export function generateWebPageSchema(page) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": page.title,
    "description": page.description,
    "url": `${SITE_URL}${page.url}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "CampusGenie",
      "url": SITE_URL
    },
    "inLanguage": "en-NG",
    "dateModified": new Date().toISOString()
  };
}
